/**
 * Les opérations de dessin, sans React et sans DOM autre que le contexte 2D.
 *
 * Tout ce qui est calculable sans canvas (couleurs, contraintes de touche
 * `Maj`, remplissage) est exporté à part et testé unitairement : c'est la
 * partie où une erreur est invisible à l'œil mais fausse dans un cas sur dix.
 */

/** Couleurs de la palette du skin (cf. handoff § Palette de couleurs). */
export const PALETTE = [
  '#16130f',
  '#ffffff',
  '#f97316',
  '#c2410c',
  '#ffec4a',
  '#39ff14',
  '#008b8b',
  '#0000ee',
  '#000080',
  '#b91c1c',
  '#ff8fd0',
  '#8b5a2b',
] as const;

/** Les quatre tailles de trait de la boîte classique. */
export const SIZES = [1, 3, 6, 10] as const;
export type Size = (typeof SIZES)[number];

export function isSize(value: unknown): value is Size {
  return typeof value === 'number' && (SIZES as readonly number[]).includes(value);
}

export interface Rgb {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

/** `#rgb` ou `#rrggbb` → composantes. Renvoie du noir si la chaîne est douteuse. */
export function hexToRgb(hex: string): Rgb {
  const clean = hex.trim().replace(/^#/, '');
  const full =
    clean.length === 3
      ? [...clean].map((char) => char + char).join('')
      : clean.padEnd(6, '0').slice(0, 6);
  if (!/^[0-9a-f]{6}$/i.test(full)) return { r: 0, g: 0, b: 0 };
  return {
    r: Number.parseInt(full.slice(0, 2), 16),
    g: Number.parseInt(full.slice(2, 4), 16),
    b: Number.parseInt(full.slice(4, 6), 16),
  };
}

export function rgbToHex({ r, g, b }: Rgb): string {
  const part = (value: number): string =>
    Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, '0');
  return `#${part(r)}${part(g)}${part(b)}`;
}

/** Vrai si la chaîne est une couleur hexa acceptable pour la palette. */
export function isHexColor(value: string): boolean {
  return /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim());
}

/** Normalise une saisie hexa (`f97316`, `#F97`) en `#rrggbb`. */
export function normalizeHex(value: string): string {
  return rgbToHex(hexToRgb(value));
}

/**
 * `rgb(...)` / `rgba(...)` renvoyé par `getComputedStyle` → hexa.
 * Renvoie `null` si la couleur est transparente : le pipeur doit alors
 * continuer à chercher sous l'élément.
 */
export function computedColorToHex(value: string): string | null {
  const match = /^rgba?\(([^)]+)\)$/.exec(value.trim());
  if (!match?.[1]) return null;
  const parts = match[1].split(/[\s,/]+/).filter(Boolean).map(Number);
  const [r, g, b, a] = parts;
  if (r === undefined || g === undefined || b === undefined) return null;
  if (a !== undefined && a === 0) return null;
  return rgbToHex({ r, g, b });
}

/** Contraint un segment à l'horizontale, la verticale ou 45° (touche `Maj`). */
export function constrainLine(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): { readonly x: number; readonly y: number } {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const angle = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4);
  const length = Math.hypot(dx, dy);
  return { x: x0 + Math.cos(angle) * length, y: y0 + Math.sin(angle) * length };
}

/** Rectangle d'un glisser ; `square` force le carré (touche `Maj`). */
export function rectFrom(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  square: boolean,
): { readonly x: number; readonly y: number; readonly w: number; readonly h: number } {
  let dx = x1 - x0;
  let dy = y1 - y0;
  if (square) {
    const side = Math.max(Math.abs(dx), Math.abs(dy));
    dx = Math.sign(dx || 1) * side;
    dy = Math.sign(dy || 1) * side;
  }
  return {
    x: Math.min(x0, x0 + dx),
    y: Math.min(y0, y0 + dy),
    w: Math.abs(dx),
    h: Math.abs(dy),
  };
}

/**
 * Remplissage par diffusion, en lignes horizontales (scanline).
 *
 * Travaille directement sur les octets de l'`ImageData` : c'est le seul
 * traitement du skin qui touche le pixel, et il doit rester assez rapide pour
 * une toile de plusieurs millions de pixels.
 *
 * Renvoie le nombre de pixels peints — zéro veut dire « la couleur était déjà
 * là », et l'appelant peut alors se passer d'un pas d'historique.
 */
export function floodFill(
  image: ImageData,
  startX: number,
  startY: number,
  color: Rgb,
  tolerance = 32,
): number {
  const { width, height, data } = image;
  const x0 = Math.floor(startX);
  const y0 = Math.floor(startY);
  if (x0 < 0 || y0 < 0 || x0 >= width || y0 >= height) return 0;

  const at = (x: number, y: number): number => (y * width + x) * 4;
  const start = at(x0, y0);
  const target = {
    r: data[start] ?? 0,
    g: data[start + 1] ?? 0,
    b: data[start + 2] ?? 0,
    a: data[start + 3] ?? 0,
  };

  // Rien à faire si le point de départ porte déjà exactement la couleur.
  if (target.a === 255 && target.r === color.r && target.g === color.g && target.b === color.b) {
    return 0;
  }

  const matches = (index: number): boolean => {
    const alpha = data[index + 3] ?? 0;
    // Un pixel transparent ne « ressemble » qu'à un autre pixel transparent :
    // sans ça le remplissage traverse le dessin déjà posé.
    if (target.a === 0) return alpha === 0;
    if (alpha === 0) return false;
    return (
      Math.abs((data[index] ?? 0) - target.r) <= tolerance &&
      Math.abs((data[index + 1] ?? 0) - target.g) <= tolerance &&
      Math.abs((data[index + 2] ?? 0) - target.b) <= tolerance
    );
  };

  let painted = 0;
  const stack: number[] = [x0, y0];
  while (stack.length > 0) {
    const y = stack.pop() ?? 0;
    const x = stack.pop() ?? 0;
    let left = x;
    while (left > 0 && matches(at(left - 1, y))) left -= 1;
    let right = x;
    while (right < width - 1 && matches(at(right + 1, y))) right += 1;

    let spanAbove = false;
    let spanBelow = false;
    for (let scan = left; scan <= right; scan += 1) {
      const index = at(scan, y);
      if (!matches(index)) continue;
      data[index] = color.r;
      data[index + 1] = color.g;
      data[index + 2] = color.b;
      data[index + 3] = 255;
      painted += 1;

      if (y > 0) {
        const above = matches(at(scan, y - 1));
        if (above && !spanAbove) stack.push(scan, y - 1);
        spanAbove = above;
      }
      if (y < height - 1) {
        const below = matches(at(scan, y + 1));
        if (below && !spanBelow) stack.push(scan, y + 1);
        spanBelow = below;
      }
    }
  }
  return painted;
}

/** Un point d'aérographe : décalage dans le disque, tiré au hasard. */
export function sprayOffset(radius: number, random = Math.random): { x: number; y: number } {
  const angle = random() * Math.PI * 2;
  // Racine carrée : sans elle, les points se tassent au centre du disque.
  const distance = Math.sqrt(random()) * radius;
  return { x: Math.cos(angle) * distance, y: Math.sin(angle) * distance };
}

/** Le minimum du contexte 2D dont le tampon a besoin — pour pouvoir le simuler. */
export interface StampContext {
  save: () => void;
  restore: () => void;
  translate: (x: number, y: number) => void;
  rotate: (angle: number) => void;
  scale: (x: number, y: number) => void;
  beginPath: () => void;
  moveTo: (x: number, y: number) => void;
  lineTo: (x: number, y: number) => void;
  closePath: () => void;
  ellipse: (
    x: number,
    y: number,
    rx: number,
    ry: number,
    rotation: number,
    start: number,
    end: number,
  ) => void;
  roundRect: (x: number, y: number, w: number, h: number, radii: number) => void;
  fill: () => void;
  fillStyle: string | CanvasGradient | CanvasPattern;
}

/** Boîte du dessin de Steve, reprise telle quelle du CSS (handoff § Anatomie). */
export const STEVE_BOX = { width: 300, height: 210 } as const;

/**
 * Tamponne un Steve complet, dans la même anatomie que le dessin CSS.
 *
 * Le point donné est le centre du poisson ; l'échelle et la rotation sont
 * appliquées autour de lui. L'ordre de tracé porte la profondeur, exactement
 * comme l'ordre du DOM dans `Steve.tsx` : bras arrière et queue d'abord.
 */
export function stampSteve(
  ctx: StampContext,
  x: number,
  y: number,
  scale: number,
  rotation: number,
  orange = '#f97316',
  dark = '#c2410c',
): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  ctx.scale(scale, scale);
  ctx.translate(-STEVE_BOX.width / 2, -STEVE_BOX.height / 2);

  // 1. Bras arrière : pivote autour de son extrémité droite, comme en CSS.
  ctx.save();
  ctx.translate(74, 119);
  ctx.rotate((-40 * Math.PI) / 180);
  ctx.fillStyle = dark;
  ctx.beginPath();
  ctx.roundRect(-60, -7, 60, 14, 7);
  ctx.fill();
  ctx.restore();

  // 2. Queue : triangle dont la pointe rentre dans le corps.
  ctx.fillStyle = orange;
  ctx.beginPath();
  ctx.moveTo(-38, 50);
  ctx.lineTo(-38, 122);
  ctx.lineTo(18, 86);
  ctx.closePath();
  ctx.fill();

  // 3. Corps.
  ctx.beginPath();
  ctx.ellipse(118, 85, 118, 67, 0, 0, Math.PI * 2);
  ctx.fill();

  // 4. Œil.
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.ellipse(184, 70, 22, 22, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#16130f';
  ctx.beginPath();
  ctx.ellipse(184, 70, 10, 10, 0, 0, Math.PI * 2);
  ctx.fill();

  // 5. Bras avant : pivote autour de son extrémité gauche.
  ctx.save();
  ctx.translate(150, 119.5);
  ctx.rotate((42 * Math.PI) / 180);
  ctx.fillStyle = orange;
  ctx.beginPath();
  ctx.roundRect(0, -7.5, 66, 15, 7.5);
  ctx.fill();
  ctx.restore();

  // 6. Jambes.
  ctx.fillStyle = orange;
  ctx.beginPath();
  ctx.roundRect(68, 144, 17, 54, 4);
  ctx.fill();
  ctx.beginPath();
  ctx.roundRect(128, 144, 17, 54, 4);
  ctx.fill();

  // 7. Pieds.
  ctx.fillStyle = dark;
  ctx.beginPath();
  ctx.roundRect(56, 192, 38, 12, 6);
  ctx.fill();
  ctx.beginPath();
  ctx.roundRect(118, 192, 38, 12, 6);
  ctx.fill();

  ctx.restore();
}
